/** ADM-01 Tableau de bord d'administration : indicateurs filtrés par permissions et portée. */
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, Icon } from '@myron/design-system';
import { AlertOctagon, Banknote, ClipboardCheck, PackageX, ShieldCheck, Truck, type LucideIcon } from 'lucide-react';
import { get } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { EntetePage, EtatRequete, MentionSimule } from '../../../shared/ui/composants';
import { nombre } from '../../../shared/ui/format';

interface Kpis { inscriptionsATraiter?: number; virementsAttendus?: number; preparationsAFaire?: number; ruptures?: number; validationsEnAttente?: number; commandesStockInsuffisant?: number }

const TUILES: { cle: keyof Kpis; libelle: string; vers: string; icone: LucideIcon; aide: string }[] = [
  { cle: 'inscriptionsATraiter', libelle: 'Inscriptions à traiter', vers: '/admin/inscriptions', icone: ClipboardCheck, aide: 'Dossiers à vérifier manuellement' },
  { cle: 'virementsAttendus', libelle: 'Virements attendus', vers: '/admin/paiements/virements', icone: Banknote, aide: 'À rapprocher par la comptabilité' },
  { cle: 'commandesStockInsuffisant', libelle: 'Payées sans stock suffisant', vers: '/admin/commandes?statut=PAYEE_STOCK_INSUFFISANT', icone: AlertOctagon, aide: 'Décision ouverte DO-04' },
  { cle: 'preparationsAFaire', libelle: 'Préparations en cours', vers: '/admin/preparations', icone: Truck, aide: 'File de préparation de vos entrepôts' },
  { cle: 'ruptures', libelle: 'Ruptures de stock', vers: '/admin/stocks', icone: PackageX, aide: 'Produit × entrepôt sans disponible' },
  { cle: 'validationsEnAttente', libelle: 'Validations à quatre yeux', vers: '/admin/validations', icone: ShieldCheck, aide: 'Demandes d’autres utilisateurs' },
];

export function PageTableauDeBord() {
  const { session } = useSession();
  const q = useQuery({ queryKey: ['admin', 'tableau-de-bord'], queryFn: () => get<Kpis>('/admin/tableau-de-bord'), refetchInterval: 30_000 });
  return (
    <>
      <EntetePage titre={`Bonjour ${session?.utilisateur.prenom ?? ''}`} sousTitre={<>Indicateurs limités à vos droits et à vos entrepôts. <MentionSimule>Données fictives</MentionSimule></>} />
      <EtatRequete requete={q} enfants={(k) => {
        const visibles = TUILES.filter((t) => k[t.cle] !== undefined);
        return (
          <ul className="my-grille" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="Indicateurs">
            {visibles.map((t) => (
              <li key={t.cle}>
                <Card pad={false}>
                  <Link to={t.vers} className="my-kpi" style={{ textDecoration: 'none', color: 'inherit' }} data-testid={`kpi-${t.cle}`}>
                    <span className="my-ligne my-secondaire"><Icon icon={t.icone} /> {t.libelle}</span>
                    <span className="my-kpi__valeur">{nombre(k[t.cle])}</span>
                    <span className="my-legende my-secondaire">{t.aide}</span>
                  </Link>
                </Card>
              </li>
            ))}
          </ul>
        );
      }} />
    </>
  );
}
